/**
 * Monkey-patches console methods to prepend a local timestamp to every log.
 *
 * Import this as the FIRST module at the application entry point so that all
 * subsequent logs (including those emitted during bootstrap) are timestamped.
 *
 * - Idempotent: guarded so HMR / re-imports never double-wrap.
 * - Isomorphic: safe to evaluate even where `window`/`process` are absent.
 * - Non-recursive: delegates to the captured NATIVE method references.
 */
(function patchConsole() {
  const PATCH_FLAG = '__ilbConsoleTimestamped__';
  if (typeof console === 'undefined' || console[PATCH_FLAG]) return;

  // Optional kill-switch (default ON in every environment).
  try {
    if (
      typeof process !== 'undefined' &&
      process.env &&
      process.env.REACT_APP_DISABLE_LOG_TIMESTAMP === 'true'
    ) {
      return;
    }
  } catch (_) {}

  const pad = (n, w = 2) => String(n).padStart(w, '0');

  const timestamp = () => {
    const d = new Date();
    const off = -d.getTimezoneOffset(); // minutes, e.g. +330 for IST
    const sign = off >= 0 ? '+' : '-';
    const oh = pad(Math.floor(Math.abs(off) / 60));
    const om = pad(Math.abs(off) % 60);
    return (
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
      `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}` +
      `.${pad(d.getMilliseconds(), 3)} ${sign}${oh}:${om}`
    );
  };

  const METHODS = ['log', 'error', 'warn', 'info', 'debug'];

  METHODS.forEach((method) => {
    const native = typeof console[method] === 'function' ? console[method].bind(console) : null;
    if (!native) return;
    console[method] = function (...args) {
      const prefix = `[${timestamp()}]`;
      // Preserve %s/%o format-string behavior AND object expansion in devtools:
      // when the first arg is a string we prepend into it; otherwise we keep the
      // original arguments as first-class values and add the prefix separately.
      if (typeof args[0] === 'string') {
        native(`${prefix} ${args[0]}`, ...args.slice(1));
      } else {
        native(prefix, ...args);
      }
    };
  });

  Object.defineProperty(console, PATCH_FLAG, { value: true, enumerable: false });
})();



// WEBPACK FOOTER //
// ./src/utils/consolePatch.js